import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
  IconButton,
  Typography,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Divider,
} from "@mui/material";
import { X, UserPlus, Store } from "lucide-react";
import Axios from "../../../Common/api/axiosConfig";

interface CreateTenantModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export const CreateTenantModal: React.FC<CreateTenantModalProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    email: "",
    role: "tenant",
    storeName: "",
    storeUrl: "",
    number: "",
    address: "",
    description: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRoleChange = (val: string) => {
    setFormData((prev) => ({
      ...prev,
      role: val,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email.trim()) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await Axios.post("/admin/tenants", {
        email: formData.email.trim().toLowerCase(),
        role: formData.role,
        storeName: formData.storeName.trim() || undefined,
        storeUrl: formData.storeUrl.trim() || undefined,
        number: formData.number.trim() ? Number(formData.number.trim()) : undefined,
        address: formData.address.trim() || undefined,
        description: formData.description.trim() || undefined,
      });

      // Reset form
      setFormData({
        email: "",
        role: "tenant",
        storeName: "",
        storeUrl: "",
        number: "",
        address: "",
        description: "",
      });

      onCreated();
      onClose();
    } catch (err: any) {
      console.error("Error creating tenant:", err);
      setError(
        err?.response?.data?.message || "Failed to create tenant account"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <UserPlus size={20} className="text-primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Add New Tenant / User
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2.5 }}>
              {error}
            </Alert>
          )}

          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, mb: 1.5, color: "text.primary" }}
          >
            1. Account Credentials
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                label="Owner Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="e.g. storeowner@company.com"
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="create-role-label">Role</InputLabel>
                <Select
                  labelId="create-role-label"
                  label="Role"
                  value={formData.role}
                  onChange={(e) => handleRoleChange(e.target.value)}
                >
                  <MenuItem value="tenant">Tenant</MenuItem>
                  <MenuItem value="superadmin">Superadmin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <Store size={18} className="text-secondary" />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              2. Storefront Setup (Optional)
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Store Name"
                name="storeName"
                value={formData.storeName}
                onChange={handleChange}
                placeholder="e.g. Rudrix Mumbai"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Store Slug / URL"
                name="storeUrl"
                value={formData.storeUrl}
                onChange={handleChange}
                placeholder="e.g. rudrix-mumbai"
                helperText="Sub-route or custom domain"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="WhatsApp Phone Number"
                name="number"
                value={formData.number}
                onChange={handleChange}
                placeholder="e.g. 919876543210"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Location / City"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="e.g. Mumbai, India"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                label="Store Tagline or Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Short description for the storefront header"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            size="small"
            disabled={loading}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="small"
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={16} /> : <UserPlus size={16} />
            }
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Create Tenant
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateTenantModal;
